import type { HarmonyPackage, HarmonyPackageBoard, HarmonyPackageDocument, HarmonyWorkitem } from '@rhythmic/db/types'
import type { HarmonyPackageFolder } from '@rhythmic/types'
import type { Collection, UseLiveQueryReturn } from '@tanstack/vue-db'
import type { Hookable } from 'hookable'
import type { Ref } from 'vue'

import type { HarmonyItem } from '../../../queries'
import type { HarmonyPlanContextEvents } from '../useHarmonyPlanContext.events.schema'

type HarmonyPlanContextHookMap = {
  [K in keyof HarmonyPlanContextEvents]: (event: HarmonyPlanContextEvents[K]) => void | Promise<void>
}

/**
 * Handle for removing a previously registered harmony plan context hook.
 */
type HarmonyPlanContextUnsubscribe = () => void

interface HarmonyPlanContextLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
  warn: (message: string, meta?: Record<string, unknown> | unknown) => void
}

interface HarmonyPlanContextQueryFactories {
  createDocumentsQuery: (planId: string) => UseLiveQueryReturn<HarmonyPackageDocument> | undefined
  createBoardsQuery: (planId: string) => UseLiveQueryReturn<HarmonyPackageBoard> | undefined
  createFoldersQuery: (planId: string) => UseLiveQueryReturn<HarmonyPackageFolder> | undefined
  createWorkitemsQuery: (planId: string, teamId: string) => UseLiveQueryReturn<HarmonyItem> | undefined
  createHarmonyPlanCollection: (
    planId: string,
    teamId: string,
    workspaceId: string,
  ) => Collection<HarmonyPackage> | undefined
}

interface UseHarmonyPlanContextOnContextChangedParams {
  hooks: Hookable<HarmonyPlanContextHookMap>
  logger: HarmonyPlanContextLoggerLike
  harmonyDocumentsQuery: Ref<UseLiveQueryReturn<HarmonyPackageDocument> | undefined>
  harmonyBoardsQuery: Ref<UseLiveQueryReturn<HarmonyPackageBoard> | undefined>
  harmonyFoldersQuery: Ref<UseLiveQueryReturn<HarmonyPackageFolder> | undefined>
  harmonyWorkitemQuery: Ref<UseLiveQueryReturn<HarmonyItem> | undefined>
  harmonyPackageCollection: Ref<Collection<HarmonyPackage> | undefined>
  harmonyWorkitemCollection: Ref<Collection<HarmonyWorkitem> | undefined>
  harmonyBoardCollection: Ref<Collection<HarmonyPackageBoard> | undefined>
  harmonyDocumentCollection: Ref<Collection<HarmonyPackageDocument> | undefined>
  clearPlanScopedQueries: () => void
  factories: HarmonyPlanContextQueryFactories
}

/**
 * Internal handler: sets up plan-scoped queries/collections when plan/team/workspace identifiers change.
 */
export default function useHarmonyPlanContextOnContextChanged({
  hooks,
  logger,
  harmonyDocumentsQuery,
  harmonyBoardsQuery,
  harmonyFoldersQuery,
  harmonyWorkitemQuery,
  harmonyPackageCollection,
  harmonyWorkitemCollection,
  harmonyBoardCollection,
  harmonyDocumentCollection,
  clearPlanScopedQueries,
  factories,
}: UseHarmonyPlanContextOnContextChangedParams) {
  let activeQueryKey: string | null = null

  const unsubscribe: HarmonyPlanContextUnsubscribe = hooks.hook(
    'ContextChanged',
    ({ harmonyPlanId, previousHarmonyPlanId, teamId, previousTeamId, workspaceId, previousWorkspaceId }) => {
      logger.debug('Harmony plan context changed, updating queries', {
        previousHarmonyPlanId,
        harmonyPlanId,
        previousTeamId,
        teamId,
        previousWorkspaceId,
        workspaceId,
      })

      if (!harmonyPlanId || !teamId) {
        if (activeQueryKey !== null) {
          logger.debug('Clearing harmony plan context queries: missing planId or teamId', {
            harmonyPlanId,
            teamId,
          })
        }

        activeQueryKey = null
        clearPlanScopedQueries()
        return
      }

      const nextKey = `${teamId}:${harmonyPlanId}`
      if (activeQueryKey === nextKey) return

      activeQueryKey = nextKey
      harmonyDocumentsQuery.value = factories.createDocumentsQuery(harmonyPlanId)
      harmonyBoardsQuery.value = factories.createBoardsQuery(harmonyPlanId)
      harmonyFoldersQuery.value = factories.createFoldersQuery(harmonyPlanId)
      harmonyWorkitemQuery.value = factories.createWorkitemsQuery(harmonyPlanId, teamId)

      harmonyDocumentCollection.value = harmonyDocumentsQuery.value?.collection as
        | Collection<HarmonyPackageDocument>
        | undefined
      harmonyBoardCollection.value = harmonyBoardsQuery.value?.collection as Collection<HarmonyPackageBoard> | undefined
      harmonyWorkitemCollection.value = harmonyWorkitemQuery.value?.collection as
        | Collection<HarmonyWorkitem>
        | undefined

      if (!workspaceId) {
        logger.warn('Missing workspace identifier for harmony plan collection', { planId: harmonyPlanId })
        harmonyPackageCollection.value = undefined
        return
      }

      harmonyPackageCollection.value = factories.createHarmonyPlanCollection(harmonyPlanId, teamId, workspaceId)
    },
  )

  return unsubscribe
}
